import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import AdminNavBar from "../components/AdminNavBar";
import Toast from "../components/Toast";
import {
  AdminEmptyState,
  AdminLoadingState,
  AdminPageBody,
  AdminSectionHeader,
  AdminTablePanel,
  adminButton,
  adminPrimaryButton,
} from "../components/AdminPageShell";
import StaffPagination from "./components/staff/StaffPagination";
import { useAdminActivityLogs } from "../store/admin";
import { formatDate } from "./components/invoices/invoiceUtils";

const PAGE_SIZE = 10;

const ACTOR_TYPES = [
  { value: "", label: "All actors" },
  { value: "ADMIN", label: "Admin" },
  { value: "STAFF", label: "Staff" },
  { value: "RESOURCE_PERSON", label: "Resource person" },
  { value: "LEARNER", label: "Learner" },
  { value: "SYSTEM", label: "System" },
];

function metadataSummary(metadata) {
  if (metadata === null || metadata === undefined) return "—";
  if (typeof metadata === "string") return metadata;
  try {
    const text = JSON.stringify(metadata);
    return text.length > 80 ? `${text.slice(0, 77)}…` : text;
  } catch {
    return "—";
  }
}

function csvCell(value) {
  const text = String(value ?? "");
  return `"${text.replace(/"/g, '""')}"`;
}

const Reports = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [action, setAction] = useState("");
  const [actionInput, setActionInput] = useState("");
  const [targetType, setTargetType] = useState("");
  const [actorType, setActorType] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [sort, setSort] = useState("newest");
  const [toast, setToast] = useState(null);
  const [isSuccess, setIsSuccess] = useState(true);

  const {
    activityLogs: logs,
    activityLogsLoading: loading,
    activityLogsError: error,
    activityLogTotal: total,
    activityLogPages: totalPages,
    fetchActivityLogs,
  } = useAdminActivityLogs();

  const showToast = useCallback((message, success = true) => {
    setToast(message);
    setIsSuccess(success);
    setTimeout(() => setToast(null), 5000);
  }, []);

  const loadLogs = useCallback(async () => {
    const result = await fetchActivityLogs({
      page,
      limit: PAGE_SIZE,
      action: action || undefined,
      targetType: targetType || undefined,
      actorType: actorType || undefined,
      from: from ? new Date(`${from}T00:00:00`).toISOString() : undefined,
      to: to ? new Date(`${to}T23:59:59`).toISOString() : undefined,
      sort,
    });
    if (!result.success) {
      showToast(result.error || "Failed to load activity logs", false);
    }
  }, [action, actorType, fetchActivityLogs, from, page, showToast, sort, targetType, to]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const stats = useMemo(() => {
    const actors = new Set();
    const targets = new Set();
    logs.forEach((log) => {
      if (log.actorType) actors.add(log.actorType);
      if (log.targetType) targets.add(log.targetType);
    });
    return {
      total,
      pageCount: logs.length,
      actors: actors.size,
      targets: targets.size,
    };
  }, [logs, total]);

  const handleApplyFilters = (event) => {
    event.preventDefault();
    setPage(1);
    setAction(actionInput.trim());
  };

  const handleReset = () => {
    setAction("");
    setActionInput("");
    setTargetType("");
    setActorType("");
    setFrom("");
    setTo("");
    setSort("newest");
    setPage(1);
  };

  const handleExport = () => {
    if (logs.length === 0) {
      showToast("There are no loaded rows to export", false);
      return;
    }
    const header = ["actorType", "action", "targetType", "metadata", "createdAt"];
    const rows = logs.map((log) =>
      [
        log.actorType,
        log.action,
        log.targetType,
        JSON.stringify(log.metadata),
        log.createdAt,
      ]
        .map(csvCell)
        .join(",")
    );
    const blob = new Blob([[header.join(","), ...rows].join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `activity-logs-page-${page}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const statCards = [
    { label: "Total Entries", value: stats.total, note: "matching filters" },
    { label: "Loaded Rows", value: stats.pageCount, note: `page ${page} of ${totalPages}` },
    { label: "Actor Types", value: stats.actors, note: "on this page" },
    { label: "Target Types", value: stats.targets, note: "on this page" },
  ];

  return (
    <div>
      <AdminNavBar title="Reports" />
      <AdminPageBody>
        {toast && <Toast message={toast} onClose={() => setToast(null)} isSuccess={isSuccess} />}

        <AdminSectionHeader
          title="Audit Logs & Activity"
          description="Every administrative action across the platform."
          count={`${total} entries`}
          actions={
            <>
              <button className={adminButton} onClick={handleExport}>
                Export CSV
              </button>
              <button className={adminButton} onClick={() => window.print()}>
                Print
              </button>
            </>
          }
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {statCards.map((card) => (
            <div
              key={card.label}
              className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                {card.label}
              </p>
              <p className="mt-1 text-2xl font-bold text-slate-900">{card.value}</p>
              <p className="text-xs text-slate-500">{card.note}</p>
            </div>
          ))}
        </div>

        <form
          onSubmit={handleApplyFilters}
          className="flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
        >
          <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Action contains
            <input
              type="text"
              value={actionInput}
              onChange={(event) => setActionInput(event.target.value)}
              placeholder="e.g. approved"
              className="h-10 w-48 rounded-lg border border-slate-300 px-3 text-sm font-normal normal-case tracking-normal text-slate-700"
            />
          </label>

          <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Target type
            <input
              type="text"
              value={targetType}
              onChange={(event) => {
                setTargetType(event.target.value);
                setPage(1);
              }}
              placeholder="e.g. Course"
              className="h-10 w-40 rounded-lg border border-slate-300 px-3 text-sm font-normal normal-case tracking-normal text-slate-700"
            />
          </label>

          <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Actor
            <select
              value={actorType}
              onChange={(event) => {
                setActorType(event.target.value);
                setPage(1);
              }}
              className="h-10 rounded-lg border border-slate-300 px-3 text-sm font-normal normal-case tracking-normal text-slate-700"
            >
              {ACTOR_TYPES.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
            From
            <input
              type="date"
              value={from}
              onChange={(event) => {
                setFrom(event.target.value);
                setPage(1);
              }}
              className="h-10 rounded-lg border border-slate-300 px-3 text-sm font-normal normal-case tracking-normal text-slate-700"
            />
          </label>

          <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
            To
            <input
              type="date"
              value={to}
              onChange={(event) => {
                setTo(event.target.value);
                setPage(1);
              }}
              className="h-10 rounded-lg border border-slate-300 px-3 text-sm font-normal normal-case tracking-normal text-slate-700"
            />
          </label>

          <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Sort
            <select
              value={sort}
              onChange={(event) => {
                setSort(event.target.value);
                setPage(1);
              }}
              className="h-10 rounded-lg border border-slate-300 px-3 text-sm font-normal normal-case tracking-normal text-slate-700"
            >
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
            </select>
          </label>

          <button type="submit" className={adminPrimaryButton}>
            Apply Filters
          </button>
          <button type="button" className={adminButton} onClick={handleReset}>
            Reset
          </button>
        </form>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">
            {error}
          </div>
        )}

        {loading ? (
          <AdminLoadingState />
        ) : logs.length === 0 ? (
          <AdminEmptyState
            title="No activity found"
            description="Try widening the date range or clearing the filters."
          />
        ) : (
          <AdminTablePanel>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="px-4 py-2 text-xs font-semibold uppercase text-slate-600">
                      Actor
                    </th>
                    <th className="px-4 py-2 text-xs font-semibold uppercase text-slate-600">
                      Action
                    </th>
                    <th className="px-4 py-2 text-xs font-semibold uppercase text-slate-600">
                      Target
                    </th>
                    <th className="px-4 py-2 text-xs font-semibold uppercase text-slate-600">
                      Metadata
                    </th>
                    <th className="px-4 py-2 text-xs font-semibold uppercase text-slate-600">
                      Date &amp; Time
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr
                      key={log.id}
                      className="cursor-pointer border-b border-slate-100 hover:bg-slate-50"
                      onClick={() => navigate(`/admin/activity-logs/${log.id}`)}
                    >
                      <td className="px-4 py-2 text-sm font-medium text-slate-800">
                        {log.actorType}
                      </td>
                      <td className="px-4 py-2">
                        <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">
                          {log.action}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-sm text-slate-800">
                        {log.targetType}
                      </td>
                      <td className="max-w-[260px] truncate px-4 py-2 text-xs text-slate-500">
                        {metadataSummary(log.metadata)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-xs text-slate-500">
                        {formatDate(log.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </AdminTablePanel>
        )}

        {totalPages > 1 && (
          <StaffPagination page={page} lastPage={totalPages} onPageChange={setPage} />
        )}
      </AdminPageBody>
    </div>
  );
};

export default Reports;
